/**
 * Calendly-Style Month Calendar Component
 * 
 * Displays a month view with available dates marked by blue circles.
 * Clicking a date selects it and shows available time slots.
 */

import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendlyCalendarProps {
  /** Set of available dates in YYYY-MM-DD format */
  availableDates: Set<string>;
  /** Currently selected date in YYYY-MM-DD format */
  selectedDate: string | null;
  /** Called when a date is clicked */
  onDateSelect: (date: string) => void;
  /** Current month in YYYY-MM format */
  month: string;
  /** Called when navigating to a different month */
  onMonthChange: (month: string) => void;
  /** Minimum selectable date (usually today) */
  minDate?: string;
  /** Maximum selectable date (booking window limit) */
  maxDate?: string;
  /** Loading state */
  loading?: boolean;
}

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const CalendlyCalendar: React.FC<CalendlyCalendarProps> = ({
  availableDates,
  selectedDate,
  onDateSelect,
  month,
  onMonthChange,
  minDate,
  maxDate,
  loading = false,
}) => {
  // Parse the current month
  const [year, monthNum] = month.split('-').map(Number);
  
  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, monthNum - 1, 1);
    const lastDay = new Date(year, monthNum, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days: (number | null)[] = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  }, [year, monthNum]);

  // Format date as YYYY-MM-DD
  const formatDate = (day: number): string => {
    return `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Check if a date is in the past or outside booking window
  const isDateDisabled = (day: number): boolean => {
    const dateStr = formatDate(day);
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  // Check if a date is today
  const isToday = (day: number): boolean => {
    const today = new Date();
    return (
      today.getFullYear() === year &&
      today.getMonth() === monthNum - 1 &&
      today.getDate() === day
    );
  };

  // Navigate to previous month
  const goToPrevMonth = () => {
    const prevMonth = monthNum === 1 ? 12 : monthNum - 1;
    const prevYear = monthNum === 1 ? year - 1 : year;
    onMonthChange(`${prevYear}-${String(prevMonth).padStart(2, '0')}`);
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const nextMonth = monthNum === 12 ? 1 : monthNum + 1;
    const nextYear = monthNum === 12 ? year + 1 : year;
    onMonthChange(`${nextYear}-${String(nextMonth).padStart(2, '0')}`);
  };

  // Check if we can navigate to previous month
  const canGoPrev = useMemo(() => {
    if (!minDate) return true;
    const [minYear, minMonth] = minDate.split('-').map(Number);
    return year > minYear || (year === minYear && monthNum > minMonth);
  }, [minDate, year, monthNum]);

  // Check if we can navigate to next month
  const canGoNext = useMemo(() => {
    if (!maxDate) return true;
    const [maxYear, maxMonth] = maxDate.split('-').map(Number);
    return year < maxYear || (year === maxYear && monthNum < maxMonth);
  }, [maxDate, year, monthNum]);

  // Format month header
  const monthName = new Date(year, monthNum - 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="select-none max-w-lg mx-auto">
      {/* Month Navigation Header */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev || loading}
          className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-[160px] justify-center">
          {loading && (
            <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {monthName}
        </h2>
        
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext || loading}
          className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Day of Week Headers */}
      <div className="grid grid-cols-7 mb-3">
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-gray-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="aspect-square p-1.5" />;
          }

          const dateStr = formatDate(day);
          const isAvailable = availableDates.has(dateStr);
          const isSelected = selectedDate === dateStr;
          const isDisabled = isDateDisabled(day);
          const isTodayDate = isToday(day);

          return (
            <div key={day} className="aspect-square p-1.5">
              <button
                onClick={() => isAvailable && !isDisabled && onDateSelect(dateStr)}
                disabled={isDisabled || !isAvailable}
                className={`
                  w-full h-full flex flex-col items-center justify-center rounded-full text-base
                  transition-all duration-150 relative
                  ${isSelected
                    ? 'bg-blue-600 text-white font-semibold'
                    : isAvailable && !isDisabled
                      ? 'text-blue-600 font-semibold hover:bg-blue-50 ring-1 ring-inset ring-blue-300'
                      : isDisabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 cursor-not-allowed'
                  }
                `}
                aria-label={`${day} ${isAvailable ? '- Available' : '- Not available'}`}
              >
                {day}
                {/* Today indicator dot */}
                {isTodayDate && !isSelected && (
                  <span className="absolute bottom-2 w-1 h-1 rounded-full bg-blue-600" />
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Timezone indicator */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <span className="font-medium">Time zone</span>
        </div>
        <p className="text-sm text-gray-600 mt-1 ml-6">
          {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')}
        </p>
      </div>
    </div>
  );
};

export default CalendlyCalendar;
